import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import {
  ApiVisa,
  BranchLocationLinkService,
  BranchService,
  LocationContainerModel,
  LocationService,
  Pagination,
} from "blg-akaun-ts-lib";
import { ReplaySubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-util-location-dropdown",
  templateUrl: "./util-location-dropdown.component.html",
  styleUrls: ["./util-location-dropdown.component.css"],
})
export class UtilLocationDropdownComponent implements OnInit {
  @Input() location: FormControl;
  @Input() apiVisa: ApiVisa;
  @Input() required: boolean;
  @Input() branchSelectedGuid: string;
  @Input() label = "Location";
  @Input() locationGuids: any[];

  @Output() locationChange = new EventEmitter<FormControl>();
  @Output() locationSelected = new EventEmitter<LocationContainerModel>();

  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  filteredOptions$: ReplaySubject<LocationContainerModel[]> = new ReplaySubject<
    LocationContainerModel[]
  >(1);
  locationList: LocationContainerModel[] = [];
  selectedLocation = new LocationContainerModel();
  locations: any = [];

  protected _onDestroy = new Subject<void>();
  unlinkedLocation: boolean;

  constructor(
    private lctnService: LocationService,
    private branchLocationLinkService: BranchLocationLinkService,
    private branchService: BranchService
  ) {}

  ngOnInit() {
    // listen for search field value changes
    this.serverSideFiltering.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.LocationFiltering();
      });

    // load the initial location list
    this.getAllLocations();
  }

  ngOnChanges() {
    if (this.branchSelectedGuid) {
      this.getAllLocations();
    }
  }

  getAllLocations() {
    //empty the prev list when selected branch changed
    this.locationList = [];
    this.locations = [];
    this.filteredOptions$.next(null);

    //if there is a selected branch
    if (this.branchSelectedGuid) {
      let defaultLocationGuid;

      this.branchService
        .getByGuid(this.branchSelectedGuid, this.apiVisa)
        .subscribe((branch) => {
          if (branch.data.bl_fi_mst_branch?.location_guid) {
            defaultLocationGuid = branch.data.bl_fi_mst_branch.location_guid;
          } else if (
            branch.data.bl_fi_mst_branch_ext.find(
              (x) => x.param_code === "MAIN_LOCATION"
            )
          ) {
            defaultLocationGuid = branch.data.bl_fi_mst_branch_ext.find(
              (x) => x.param_code === "MAIN_LOCATION"
            )?.value_string;
          }
        });

      let locationGuidsList = [];
      let pagination = new Pagination();
      pagination.limit = 1000;
      pagination.conditionalCriteria = [];
      pagination.conditionalCriteria.push({
        columnName: "guid_branch",
        operator: "=",
        value: this.branchSelectedGuid,
      });

      this.branchLocationLinkService
        .getByCriteria(pagination, this.apiVisa)
        .subscribe((response) => {
          if (response.data && response.data.length) {
            for (let i = 0; i < response.data.length; i++) {
              if (
                response.data[i].bl_fi_mst_branch_location_link
                  .guid_location !== null
              ) {
                locationGuidsList.push(
                  response.data[
                    i
                  ].bl_fi_mst_branch_location_link.guid_location.toString()
                );
              }
            }

            let paging = new Pagination();
            paging.limit = 1000;
            paging.conditionalCriteria = [];
            paging.conditionalCriteria.push({
              columnName: "hdr_guids",
              operator: "=",
              value: locationGuidsList.toString(),
            });

            if (locationGuidsList.length > 0) {
              this.lctnService
                .getByCriteria(paging, this.apiVisa)
                .subscribe((lctns) => {
                  //empty the prev list when selected branch changed
                  this.locationList = [];
                  this.locations = [];
                  this.filteredOptions$.next(null);

                  lctns.data.forEach((data) => {
                    this.locations.push(data);
                    this.locationList.push(data);
                  });

                  const allValuesDifferent = this.locations.every(
                    (item) => item.guid !== this.location.value
                  );
                  this.unlinkedLocation = !allValuesDifferent;

                  if (
                    this.location.value === null ||
                    this.location.value === undefined ||
                    lctns.data.some(
                      (x) => x.bl_inv_mst_location.guid === this.location.value
                    ) ||
                    defaultLocationGuid
                  ) {
                    if (
                      lctns.data.some(
                        (x) =>
                          x.bl_inv_mst_location.guid === this.location.value
                      )
                    ) {
                      this.location.setValue(this.location.value);
                    } else if (
                      lctns.data.some(
                        (x) =>
                          x.bl_inv_mst_location.guid === defaultLocationGuid
                      )
                    ) {
                      this.location.setValue(defaultLocationGuid);
                    } else if (lctns.data.length === 1) {
                      this.location.setValue(
                        lctns.data[0].bl_inv_mst_location.guid
                      );
                    }
                  }

                  // sorting
                  const sorted = this.locations.sort((a, b) =>
                    a.bl_inv_mst_location.code < b.bl_inv_mst_location.code
                      ? -1
                      : 1
                  );
                  //fill the drop down
                  this.filteredOptions$.next(this.locations.slice());
                });
            }
          }
        });
    }
  }

  LocationFiltering() {
    if (!this.locations) {
      return;
    }
    // get the search keyword
    let search = this.serverSideFiltering.value;
    if (!search) {
      this.filteredOptions$.next(this.locations.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the locations
    this.filteredOptions$.next(
      this.locations.filter(
        (option) =>
          (option.bl_inv_mst_location.code
            ? option.bl_inv_mst_location.code.toLowerCase().indexOf(search) > -1
            : null) ||
          (option.bl_inv_mst_location.name
            ? option.bl_inv_mst_location.name.toLowerCase().indexOf(search) > -1
            : null)
      )
    );
  }

  onSelect(locationGuid: string) {
    if (locationGuid) {
      this.location.patchValue(locationGuid);
      this.selectedLocation = this.locationList.find(
        (lctn) => lctn.bl_inv_mst_location.guid === locationGuid
      );
      this.locationSelected.emit(this.selectedLocation);
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }
}
